/**
 * Best-effort parse of a single-line US mailing address into line1 / city / state / zip.
 * Ported from skimbuster-mobile `parseUsAddressLine.ts` for identical sweep import behavior.
 */

export type ParsedUsLine = {
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
};

const TRAILING_STATE_ZIP = /,\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/;

export function parseUsAddressFromSingleLine(raw: string): ParsedUsLine {
  const s = String(raw ?? '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!s) {
    return { addressLine1: '', city: '', state: '', zip: '' };
  }

  const m = s.match(TRAILING_STATE_ZIP);
  if (!m) {
    return { addressLine1: s, city: '', state: '', zip: '' };
  }

  const state = m[1].toUpperCase();
  const zip = m[2];
  const before = s.slice(0, s.length - m[0].length).trim();

  const commaSplit = before.match(/^(.+),\s*(.+)$/);
  if (commaSplit) {
    return {
      addressLine1: commaSplit[1].trim(),
      city: commaSplit[2].trim(),
      state,
      zip,
    };
  }

  const parts = before.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const city = parts[parts.length - 1] ?? '';
    const addressLine1 = parts.slice(0, -1).join(' ');
    return { addressLine1, city, state, zip };
  }

  return { addressLine1: before, city: '', state, zip };
}
